/**
 * Générateur — Il effectue, avec des nombres décimaux relatifs, des produits et
 * des quotients.
 * Attendu ID : 146 | Niveau : 4ème | Domaine : Nombres (desc 11, O11)
 *
 * Source doc prof (NOMBRE ET CALCUL, cluster 3) : programmes de calcul évalués
 * Ex.14 « (x − 10) × (−5) + 5x » et Ex.16 ; produits/quotients de relatifs.
 * Plan de cartes validé 2026-07-14, revu 2026-09-22 et 2026-09-23 (quatre cartes) :
 *
 *   Carte « produit »   — Produit de deux décimaux relatifs, de tête (saisie)
 *   Carte « quotient »  — Quotient EXACT de décimaux relatifs, de tête (saisie)
 *   Carte « signe »     — Signe d'un produit ou d'un quotient : tableau Vrai/Faux
 *                         à 2, 3 ou 4 facteurs, et QCM sur le signe d'un quotient
 *   Carte « reecrire »  — Écrire un quotient avec un dénominateur positif (trous)
 *
 * Revue 2026-09-22 (mesures sur dix mille tirages par carte) :
 *   - SUPPRIMÉ « programme » en QCM (« que remarques-tu ? » sur l'invariant
 *     (x − a) × (−b) + bx = ab) : c'est du calcul littéral, pas un produit de
 *     décimaux relatifs. La version saisie restait alors ; elle est partie à
 *     son tour le 2026-09-23 (voir plus bas).
 *   - SUPPRIMÉ « sanscalcul » (QCM « un élève affirme que a/b = c/d ») : la
 *     bonne réponse était la même phrase à tous les tirages (fuite totale), le
 *     libellé parlait d'inégalité pour une égalité, et comparer deux rationnels
 *     relève de l'attendu 143. Le QCM de l'attendu reste « signe » (quotient).
 *   - « produit » : premier facteur décimal de valeur absolue < 3, second facteur
 *     entier (2..9) ou décimal court (< 3), jamais ±1 ; avant, 38 % des résultats
 *     avaient deux décimales, jusqu'à 98,01.
 *   - « quotient » : diviseur entier (2..9), quotient visé décimal non entier,
 *     dividende à une décimale au plus (l'exemple officiel : 7,2 ÷ (−5)) ;
 *     avant, le diviseur était décimal dans 46 % des cas.
 *   - « produit » et « quotient » : la consigne impose l'écriture décimale (une
 *     réponse en fraction était refusée sans que l'énoncé l'interdise).
 *   - « reecrire » : la parenthèse qui donnait la méthode est retirée ; carte
 *     GARDÉE (mode auto, À ARBITRER) : produire l'écriture -a/b de a/-b est la
 *     règle des signes appliquée à un quotient, le QCM ne demande que le signe.
 *   - « signe » V/F : lignes à 3 ou 4 facteurs ajoutées (exemple officiel : le
 *     signe de (−6,7)×7×(−1,24)×(−0,7)), au moins une ligne vraie et une fausse.
 *   - Parenthèses explicatives des consignes réécrites en phrases.
 *
 * Arithmétique EXACTE (toolkit rationnel de nombres.js) : aucun flottant. Le
 * quotient est rendu exact par construction (dividende = quotient × diviseur).
 * Affichage du moins typographique « − », réponses saisies au moins ASCII « - ».
 *
 * Revue 2026-09-23 (typographie, règle prof) : la dernière fraction affichée à
 * plat, la phrase à trous de « reecrire » (« 2/-11 = {{-2}}/{{11}} », que le
 * lint des énoncés ne voyait pas), passe en KaTeX : ligne globale entière
 * entre $…$, les deux fractions empilées, les trous en triple accolade
 * (fracTrouTex). Les réponses tapées restent en texte brut.
 *
 * Règle prof 2026-09-23 (composition des tableaux vrai-faux) : quatre lignes
 * au maximum, sinon c'est trop long à lire. Le tableau « signe » passe de cinq
 * lignes à quatre : deux lignes à deux facteurs (× ou ÷), une à trois, une à
 * quatre ; la cinquième, de taille tirée, disparaît. Au moins une ligne vraie
 * et une fausse, comme avant.
 *
 * Revue 2026-09-23 (calcul de tête) : le prof a demandé « comment l'élève fait
 * ça sans calculatrice ? ». Sur six tirages réels de « produit », deux ne se
 * faisaient pas de tête (2,2 × (−0,8) = −1,76 et 2,9 × (−0,6) = −1,74 : il faut
 * poser 22 × 8 et 29 × 6) ; le cas décimal × décimal, une moitié des tirages
 * par construction, ne se traitait par aucune technique puisque le dixième
 * était tiré librement entre 0,1 et 2,9. Or l'attendu exige le calcul mental :
 * son exemple de réussite officiel (attendus de fin d'année de 4e, Eduscol)
 * commence par « Il calcule mentalement » et donne −7 × 3 ; −2,5 × (−4) ;
 * 2,4 × (−0,5) ; −12,8 : 2 ; −63 : (−0,7) ; 7,2 : (−5). Ses décimaux (0,5 ;
 * 2,5 ; 0,7 ; 5) sont ceux sur lesquels une TECHNIQUE s'applique : × 0,5 c'est
 * la moitié, ÷ 5 c'est doubler puis diviser par 10, ÷ 0,7 c'est 63 ÷ 7 = 9 puis
 * la virgule. Correction :
 *   - « produit » : le facteur décimal vient de DECIMAUX_TECHNIQUE (0,1 ; 0,2 ;
 *     0,5 ; 1,5 ; 2,5, technique de chacun en commentaire de la liste) ; l'autre
 *     facteur reste un entier 2..9 ou un décimal court 0,1..2,9, et pour la
 *     famille « moitié » son dixième est pair, pour que la moitié tombe sur un
 *     dixième (2,3 × 0,5 = 1,15 ne se fait plus). Deux décimaux de la famille
 *     « moitié » ne se rencontrent jamais (0,5 × 1,5 = 0,75 exclu). Les deux
 *     ordres sont tirés, comme dans l'exemple officiel. Gardés : les signes des
 *     deux côtés, le facteur 1 exclu, l'écriture décimale imposée.
 *   - « quotient » : la revue du 22/09 avait interdit le diviseur décimal parce
 *     que les dividendes montaient à 98,01 ; c'était la valeur à borner, pas le
 *     diviseur à interdire (−63 : (−0,7) est dans l'exemple officiel). Diviseur
 *     décimal rétabli, tiré dans la même liste, une fois sur trois
 *     (DIVISEUR_DECIMAL_UNE_FOIS_SUR) ; le diviseur entier 2..9 reste
 *     majoritaire comme dans l'exemple officiel. Valeurs bornées par forme :
 *     fait de table puis virgule (5,6 ÷ 7 = 0,8), moitié (12,8 ÷ 2 = 6,4),
 *     ÷ 5 = doubler puis ÷ 10 (7,2 ÷ 5 = 1,44), dividende à une décimale sur
 *     0,1 ; 0,2 ; 0,5 (3,7 ÷ 0,1 = 37 ; 3,7 ÷ 0,2 = 18,5 ; 3,7 ÷ 0,5 = 7,4), et
 *     « combien de fois 1,5 ou 2,5 ? » (13,5 ÷ 1,5 = 9 ; 1,5 ÷ 2,5 = 0,6). La
 *     division tombe toujours juste par construction. Le quotient est entier
 *     dans un tirage sur six environ, seulement avec un diviseur décimal (comme
 *     −63 : (−0,7) = 90), jamais avec un diviseur entier.
 *   - Les deux lignes de calcul passent en KaTeX (\times, \div, virgule {,},
 *     premier nombre groupé entre accolades s'il commence par un moins).
 *   - Pas de flag calculatrice sur ces deux cartes : l'objet de la revue est
 *     qu'elles se fassent sans.
 *   - Consigne (décision prof 2026-09-23) : la phrase « Donne le résultat en
 *     écriture décimale, pas en fraction. » ajoutée le 22/09 est retirée,
 *     inutile et répétée à chaque item. C'est une dérogation à la règle rouge
 *     de docs/FORMATS_TANOED.md (la consigne impose l'écriture attendue quand
 *     plusieurs écritures justes existent), sûre ici parce que les deux
 *     opérandes de l'énoncé sont déjà en écriture décimale : le registre de la
 *     réponse est fixé par le contexte. Détail et symptôme en cas d'erreur dans
 *     le commentaire au-dessus des deux cartes ; les phrases qui disent comment
 *     taper un signe négatif, ailleurs dans le lot, ne sont pas visées.
 *
 * Suppression 2026-09-23 de la carte « programme » (saisie, « Exécuter un
 * programme de calcul », clé programme_texte). Elle affichait « Voici un
 * programme de calcul : (x − 4) × (−3) − 9. Calcule le résultat pour
 * x = (−9). » Deux raisons la sortent de cet attendu : (1) ce n'est ni un
 * produit ni un quotient mais une CHAÎNE de trois opérations avec priorités,
 * alors que l'intitulé dit « des produits et des quotients » et que l'exemple
 * de réussite officiel ne donne que des opérations simples (−7 × 3 ;
 * −2,5 × (−4) ; 2,4 × (−0,5) ; −12,8 : 2 ; −63 : (−0,7) ; 7,2 : (−5)) ;
 * (2) elle ne tirait AUCUN décimal, tout était entier, alors que l'intitulé
 * dit « avec des nombres décimaux relatifs ». C'est le second morceau de calcul
 * littéral retiré de l'attendu : sa version QCM, qui faisait démontrer
 * l'invariance d'un programme, est partie le 22/09 pour la même raison. Le
 * compte de cartes passe de cinq à quatre ; le QCM de l'attendu reste « signe ».
 * OÙ VA LE GESTE : dans gen_160, « Il démontre l'équivalence de deux programmes
 * de calcul », comme ÉCHELON D'ENTRÉE, à reprendre quand la vague des attendus
 * 153 à 200 aura lieu. Pas déplacé aujourd'hui : gen_160 n'est pas revu et
 * encore fermé côté prof ; ses trois cartes comparent deux programmes,
 * réduisent une expression et cherchent un programme au résultat constant,
 * aucune ne fait simplement EXÉCUTER un programme sur une valeur, alors qu'on
 * teste sur des nombres avant de démontrer. Pour cette vague, la carte telle
 * qu'elle était : expression (x − a) × (−b) + c avec a ∈ [2 ; 12], b ∈ [2 ; 9],
 * c ∈ ±[1 ; 9] (affiché « + c » ou « − |c| »), valeur x ∈ ±[1 ; 9] affichée
 * entre parenthèses si négative, résultat entier (x − a) × (−b) + c saisi tel
 * quel ; consigne « Voici un programme de calcul : … » puis « Calcule le
 * résultat pour x = … » ; params { a, b, c, x, res }.
 */

import { randomChoice, randInt, shuffle, makeSaisie, makeQcm, makeVraiFaux, makeTexteTrou, fracTrouTex, tex } from '../../../utils';
import { rat, ratMul, ratDiv, decExact, aff } from './nombres';
import { gcd } from '../../6eme/fractions/fraction';

const ATTENDU_ID = 146;

const L_PRODUIT = 'Effectuer un produit de décimaux relatifs';
const L_QUOTIENT = 'Effectuer un quotient de décimaux relatifs';
const L_SIGNE = 'Déterminer le signe d\'un produit ou d\'un quotient';
// Carte migrée depuis gen_155 le 2026-07-28 (voir bandeau en fin de fichier)
const L_REECRIRE = 'Écrire un quotient avec un dénominateur positif';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Fraction en KaTeX (carte « signe » QCM, migrée de gen_155). */
const dfrac = (a, b) => `$\\dfrac{${a}}{${b}}$`;
/** Couple (a, b) premiers entre eux, a ∈ [1;amax], b ∈ [2;bmax]. */
const coprime = (amax = 12, bmax = 12) => {
    for (let i = 0; i < 60; i++) {
        const a = randInt(1, amax);
        const b = randInt(2, bmax);
        if (gcd(a, b) === 1) return { a, b };
    }
    return { a: 3, b: 7 };
};

// Opérandes tirés (rationnels exacts, signés, jamais nuls, jamais ±1)
const signe = () => randomChoice([1, -1]);
/** ± décimal à UNE décimale non nulle, partie entière entMin..entMax : de entMin,1 à entMax,9. */
const decimalUneDecimale = (entMax, entMin = 0) => rat(signe() * (randInt(entMin, entMax) * 10 + randInt(1, 9)), 10);
const decOperand = () => decimalUneDecimale(9);          // 0,1 à 9,9 (« signe », dividende de « quotient »)
const intOperand = () => rat(signe() * randInt(2, 9), 1);   // ± entier 2..9
const disp = (r) => aff(decExact(r));            // « −2,5 » sans les parenthèses
const par = (r) => (r.p < 0 ? `(${disp(r)})` : disp(r));
// Consignes de « produit » et « quotient », décision prof 2026-09-23 : la phrase
// « Donne le résultat en écriture décimale, pas en fraction. » (constante
// CONSIGNE_DECIMALE, ajoutée le 22/09) est RETIRÉE, inutile et répétée à chaque
// item. C'est une dérogation à la règle rouge de docs/FORMATS_TANOED.md (« si
// plusieurs écritures justes existent, la consigne impose l'écriture attendue »),
// sans risque ici : l'énoncé donne ses deux opérandes en écriture décimale, le
// registre de la réponse est fixé par le contexte et non laissé au choix de
// l'élève (personne n'écrit 42/10 pour 2,1 × 2). La règle vise les questions qui
// n'orientent pas (« la moitié de 1 ? » : 0,5 et 1/2 se valent). Symptôme si on
// se trompait : un élève qui répondrait en fraction (« -3/10 » pour « -0,3 »)
// serait compté faux, le moteur ne lit pas les fractions (Number('-3/10') vaut
// NaN). Ne pas « réparer » en remettant la phrase.

// ═════════ Décimaux « à technique » (revue 2026-09-23, calcul de tête) ═════════
// Dans « produit » et « quotient », un décimal n'est jamais tiré librement : il
// vient de cette liste, où chacun se traite de tête par un geste nommé, en
// facteur comme en diviseur (l'exemple officiel choisit 0,5 ; 2,5 ; 0,7 ; 5).
//   0,1 : × = le dixième, la virgule recule d'un rang (2,9 × 0,1 = 0,29)
//         ÷ = × 10, la virgule avance d'un rang (3,7 ÷ 0,1 = 37)
//   0,2 : × = le double, puis le dixième (2,7 × 0,2 = 5,4 puis 0,54)
//         ÷ = on multiplie les deux nombres par 10, il reste une moitié à
//             prendre (3,7 ÷ 0,2 = 37 ÷ 2 = 18,5)
//   0,5 : × = la moitié (2,4 × 0,5 = 1,2)        ÷ = le double (3,7 ÷ 0,5 = 7,4)
//   1,5 : × = le nombre plus sa moitié (2,8 × 1,5 = 2,8 + 1,4 = 4,2)
//         ÷ = « combien de fois 1,5 ? », ou on multiplie les deux nombres par 2
//             (13,5 ÷ 1,5 = 27 ÷ 3 = 9)
//   2,5 : × = le double plus la moitié (2,8 × 2,5 = 5,6 + 1,4 = 7)
//         ÷ = « combien de fois 2,5 ? », ou on multiplie les deux nombres par 4
//             (17,5 ÷ 2,5 = 70 ÷ 10 = 7)
// Famille « virgule » (0,1 ; 0,2) : tout partenaire convient. Famille « moitié »
// (0,5 ; 1,5 ; 2,5) : le partenaire doit avoir une moitié à un seul dixième,
// entier ou dixième pair, sinon 2,3 × 0,5 = 1,15 n'est plus un calcul de tête.
const TECHNIQUE_VIRGULE = [rat(1, 10), rat(1, 5)];          // 0,1 ; 0,2
const TECHNIQUE_MOITIE = [rat(1, 2), rat(3, 2), rat(5, 2)];  // 0,5 ; 1,5 ; 2,5
const DECIMAUX_TECHNIQUE = [...TECHNIQUE_VIRGULE, ...TECHNIQUE_MOITIE];
const estFamilleMoitie = (t) => t.q === 2;       // 0,5 ; 1,5 ; 2,5 ont 2 pour dénominateur réduit
const signer = (r) => rat(signe() * r.p, r.q);
// « quotient » : diviseur décimal une fois sur trois ; entier sinon, le cas
// majoritaire de l'exemple officiel (−12,8 : 2 ; 7,2 : (−5) ; −63 : (−0,7)).
const DIVISEUR_DECIMAL_UNE_FOIS_SUR = 3;

// Ligne KaTeX « a × b = … » : virgule collée ({,}), premier nombre groupé s'il
// commence par un moins, second entre parenthèses s'il est négatif.
const kNum = (r) => decExact(r).replace(',', '{,}');
const ligneOperation = (A, op, B) => {
    const a = kNum(A);
    const b = kNum(B);
    return tex(`${A.p < 0 ? `{${a}}` : a} ${op} ${B.p < 0 ? `(${b})` : b} = \\ldots`);
};

// ═════════ Carte « produit » — produit de deux décimaux relatifs (saisie) ═════════

// Partenaire du décimal à technique : entier 2..9 une fois sur deux, sinon
// décimal court 0,1..2,9 ; pour la famille « moitié », dixième pair (0,2..2,8).
const partenaireProduit = (moitie) => {
    if (randomChoice([true, false])) return intOperand();
    const dixieme = moitie ? randomChoice([2, 4, 6, 8]) : randInt(1, 9);
    return rat(signe() * (randInt(0, 2) * 10 + dixieme), 10);
};

const genProduit = () => {
    const t = randomChoice(DECIMAUX_TECHNIQUE);
    const T = signer(t);
    const P = partenaireProduit(estFamilleMoitie(t));
    const [A, B] = shuffle([T, P]);              // les deux ordres : −2,5 × (−4) et 2,4 × (−0,5)
    const res = ratMul(A, B);
    const rep = decExact(res);
    const payload = makeSaisie(rep,
        '<p>Calcule ce produit de nombres relatifs.</p>'
        + `<p>${ligneOperation(A, '\\times', B)}</p>`,
        opts(L_PRODUIT));
    payload._meta.params = { a: decExact(A), b: decExact(B), rep, technique: decExact(t) };
    return payload;
};

// ═════════ Carte « quotient » — quotient EXACT (saisie) ═════════

// Forme du couple (quotient visé, diviseur) ; le dividende en découle, donc la
// division tombe juste par construction.
const tirerQuotient = () => {
    if (randInt(1, DIVISEUR_DECIMAL_UNE_FOIS_SUR) === 1) {
        const t = randomChoice(DECIMAUX_TECHNIQUE);
        const D = signer(t);
        if (estFamilleMoitie(t) && t.p !== 1) {
            // 1,5 et 2,5 : « combien de fois ? ». Quotient entier 2..9 ou décimal à
            // dixième pair 0,2..1,8 (un dixième impair donnerait un dividende à
            // deux décimales : 1,5 × 0,3 = 0,45).
            const Q = randomChoice([true, false]) ? intOperand() : rat(signe() * randomChoice([2, 4, 6, 8, 12, 14, 16, 18]), 10);
            return { Q, D, forme: 'combien_de_fois' };
        }
        // 0,1 ; 0,2 ; 0,5 : dividende décimal 0,1..9,9 à une décimale, le
        // quotient s'en déduit (× 10, × 5, × 2).
        return { Q: ratDiv(decOperand(), D), D, forme: 'virgule' };
    }
    const D = intOperand();
    const abs = Math.abs(D.p);
    if (abs === 2 && randomChoice([true, false])) {
        return { Q: decimalUneDecimale(9, 1), D, forme: 'moitie' };            // 12,8 ÷ 2 = 6,4 (exemple officiel)
    }
    if (abs === 5 && randomChoice([true, false])) {
        return { Q: ratDiv(decimalUneDecimale(9, 1), D), D, forme: 'cinquieme' };   // 7,2 ÷ 5 = 1,44 (exemple officiel)
    }
    return { Q: rat(signe() * randInt(1, 9), 10), D, forme: 'table' };       // 5,6 ÷ 7 = 0,8 : un fait de table, puis la virgule
};

const genQuotient = () => {
    const { Q, D, forme } = tirerQuotient();
    const dividende = ratMul(Q, D);               // division exacte par construction
    const rep = decExact(Q);
    const payload = makeSaisie(rep,
        '<p>Calcule ce quotient. La division tombe juste.</p>'
        + `<p>${ligneOperation(dividende, '\\div', D)}</p>`,
        opts(L_QUOTIENT));
    payload._meta.params = { dividende: decExact(dividende), d: decExact(D), rep, forme };
    return payload;
};

// ═════════ Carte « signe » — signe d'un produit/quotient (tableau V/F) ═════════

// Repli déterministe (jamais atteint en pratique) : trois lignes fixes distinctes.
const REPLI_SIGNE = [
    ['(−2) × 3 est négatif.', true],
    ['(−2) × (−3) est négatif.', false],
    ['6 ÷ (−2) est positif.', false],
];

const genSigneVf = () => {
    const lignes = [];
    const bonnes = [];
    const used = new Set();
    // 4 lignes (règle prof 2026-09-23) : deux à deux facteurs (× ou ÷), une à
    // trois et une à quatre facteurs (produit, revue 2026-09-22), dans un ordre
    // mélangé ; au moins une ligne vraie et une fausse.
    const tailles = shuffle([2, 2, 3, 4]);
    const verites = shuffle([true, false, ...tailles.slice(2).map(() => randomChoice([true, false]))]);
    let secu = 0;
    while (lignes.length < tailles.length && secu++ < 200) {
        const k = lignes.length;
        const facteurs = Array.from({ length: tailles[k] }, () => randomChoice([decOperand(), intOperand()]));
        const op = tailles[k] === 2 ? randomChoice(['×', '÷']) : '×';
        const key = facteurs.map(f => `${f.p}/${f.q}`).join(op);
        if (used.has(key)) continue;
        used.add(key);
        const signeNeg = facteurs.filter(f => f.p < 0).length % 2 === 1;   // nombre impair de négatifs → négatif
        const adjVrai = signeNeg ? 'négatif' : 'positif';
        const adj = verites[k] ? adjVrai : (signeNeg ? 'positif' : 'négatif');
        lignes.push(`${facteurs.map(par).join(` ${op} `)} est ${adj}.`);
        bonnes.push(verites[k] ? 0 : 1);
    }
    REPLI_SIGNE.forEach(([txt, vrai]) => {
        if (lignes.length < 3 && !lignes.includes(txt)) { lignes.push(txt); bonnes.push(vrai ? 0 : 1); }
    });
    const ordre = shuffle(lignes.map((_, i) => i));
    const L = ordre.map(i => lignes[i]);
    const Bn = ordre.map(i => bonnes[i]);
    const payload = makeVraiFaux(L, Bn,
        '<p>La règle des signes s\'applique aussi à la division.</p>'
        + '<p><b>Vrai ou faux ?</b></p>', opts(L_SIGNE));
    payload._meta.params = { lignes: L, bonnes: Bn };
    return payload;
};

// ═══════════════════════════════════════════════════════════════
// Cartes MIGRÉES depuis gen_155 le 2026-07-28
// ───────────────────────────────────────────────────────────────
// Motif : l'attendu 155 est « utiliser les nombres premiers pour reconnaître
// et produire des fractions égales » ; ces cartes ne mobilisent ni les
// premiers ni les produits en croix mais la RÈGLE DES SIGNES. Or l'attendu 146
// (« il effectue, avec des nombres décimaux relatifs, des produits et des
// quotients ») porte explicitement, dans ses exemples de réussite éduscol :
// « il détermine le signe de (−6,7)×7×(−1,24)×(−0,7) et de 11,4×(−3,5)/−(5,6×123) ».
// Elles rejoignent donc la carte « signe » déjà présente ici (tableau V/F),
// dont le QCM devient un second format. Trois cartes migrées ; la troisième,
// « sanscalcul », a été supprimée à la revue du 2026-09-22 (voir en-tête).
// ═══════════════════════════════════════════════════════════════

// ═════════ Carte « signe » (2ᵉ format) — Signe d'un quotient (QCM) ═════════

const SG_POS = 'Positif';
const SG_NEG = 'Négatif';
// « Nul » remplacé (fix rapport 4e, 2026-07-28) : num et den ne valant jamais 0,
// le quotient n'est jamais nul → distracteur éliminable sans faire de maths.
// Le nouveau libellé cible la misconception « il faut effectuer la division pour
// conclure », alors que la règle des signes suffit — donc toujours faux. (Les
// valeurs étant AFFICHÉES dans l'énoncé, « sans les valeurs » n'aurait aucun
// sens ici.)
const SG_INDECIDABLE = 'On ne peut pas savoir sans faire le calcul';

const genSigneQcm = () => {
    const sNum = randomChoice([1, -1]);
    const sDen = randomChoice([1, -1]);
    const num = sNum * randInt(1, 13);
    const den = sDen * randInt(2, 13);
    const correct = (num * den > 0) ? SG_POS : SG_NEG;
    const texte = `<p><b>Le quotient ${dfrac(num, den)} est-il positif ou négatif ?</b></p>`;
    const payload = makeQcm([SG_POS, SG_NEG, SG_INDECIDABLE], correct, texte, opts(L_SIGNE));
    payload._meta.params = { num, den };
    return payload;
};

// ═════════ Carte « reecrire » — Dénominateur positif (trous) ═════════

const genReecrireTrous = () => {
    const { a, b } = coprime();               // a/b irréductible
    const sNum = randomChoice([1, -1]);
    const num0 = sNum * a;
    const den0 = -b;                           // dénominateur NÉGATIF → il y a du travail
    const numRep = -num0;                      // × (−1) haut et bas
    const denRep = -den0;                      // = b > 0
    // Ligne KaTeX globale (revue 2026-09-23) : le quotient donné, dénominateur
    // négatif compris, puis « = » et la fraction attendue avec ses deux trous.
    const phrase = `$\\dfrac{${num0}}{${den0}} = ${fracTrouTex(`{{${numRep}}}`, `{{${denRep}}}`)}$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Réécris ce quotient avec un dénominateur positif.</b></p>'
        + '<p>Donne la fraction irréductible : le numérateur, puis le dénominateur.</p>',
        { ...opts(L_REECRIRE), mode: 'saisie' });
    payload._meta.params = { num0, den0, numRep, denRep };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    produit_texte: genProduit,
    quotient_texte: genQuotient,
    signe_tableau_vf: genSigneVf,
    signe_qcm_unique: genSigneQcm,               // migré de gen_155
    reecrire_texte_trou_saisie: genReecrireTrous, // migré de gen_155
};

const FORMAT_LABELS = {
    texte: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux',
    texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'produit', label: L_PRODUIT, etapes: 2, formatsList: buildFormats('produit') },
    { id: 'quotient', label: L_QUOTIENT, etapes: 2, formatsList: buildFormats('quotient') },
    { id: 'signe', label: L_SIGNE, etapes: 1, formatsList: buildFormats('signe') },
    { id: 'reecrire', label: L_REECRIRE, etapes: 1, formatsList: buildFormats('reecrire') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
